import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge, EmptyState } from "@/components/common";
import { DatePicker } from "@/components/DatePicker";
import { VisitCalendar } from "@/components/VisitCalendar";
import { ReportImportDialog } from "@/components/ReportImportDialog";
import { STATUS_META, PRESTATIONS, PRESTATION_LIST, fmtDate, todayIso } from "@/lib/helpers";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const EMPTY_SITE = { name: "", address: "", commune: "", on_site_contact: "", access_instructions: "" };

function CreateVisitDialog({ open, onOpenChange, preset }) {
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const canManage = hasPerm("visits.manage");
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [clientId, setClientId] = useState(preset?.client || "");
  const [siteId, setSiteId] = useState(preset?.site || "");
  const [date, setDate] = useState(todayIso());
  const [heure, setHeure] = useState("");
  const [prestations, setPrestations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [assignedTo, setAssignedTo] = useState("");
  const [addingSite, setAddingSite] = useState(false);
  const [siteForm, setSiteForm] = useState(EMPTY_SITE);

  useEffect(() => { if (open) api.get("/clients").then((r) => setClients(r.data)); }, [open]);
  useEffect(() => {
    if (open && canManage) {
      api.get("/users").then((r) => setUsers(r.data.filter((u) => u.is_active !== false)));
      setAssignedTo((a) => a || user?.id || "");
    }
  }, [open, canManage, user]);
  const loadSites = useCallback(() => {
    if (clientId) api.get("/sites", { params: { client_id: clientId } }).then((r) => setSites(r.data));
    else setSites([]);
  }, [clientId]);
  useEffect(() => { loadSites(); setAddingSite(false); }, [loadSites]);

  const createSite = async () => {
    if (!siteForm.name.trim()) { toast.error("Le nom du site est requis"); return; }
    try {
      const { data } = await api.post("/sites", { client_id: clientId, ...siteForm });
      setSites((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setSiteId(data.id);
      setAddingSite(false); setSiteForm(EMPTY_SITE);
      toast.success("Site ajouté — il apparaît aussi dans la fiche client");
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { if (preset?.client) setClientId(preset.client); if (preset?.site) setSiteId(preset.site); }, [preset]);

  const toggle = (p) => setPrestations((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/visits", { client_id: clientId, site_id: siteId, date, scheduled_time: heure || null, prestations, assigned_to: assignedTo || undefined });
      toast.success(`Visite ${data.reference} créée`);
      onOpenChange(false);
      navigate(`/check/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="create-visit-dialog">
        <DialogHeader><DialogTitle>Nouvelle visite avant devis</DialogTitle>
          <DialogDescription>Programmez la visite (date à venir possible) et rattachez-la à un client et un site.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Client *</Label>
            <Select value={clientId} onValueChange={(v) => { setClientId(v); setSiteId(""); }}>
              <SelectTrigger data-testid="cv-client"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Site *</Label>
              {clientId && !addingSite && (
                <button type="button" onClick={() => setAddingSite(true)} className="flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline" data-testid="cv-newsite-btn">
                  <Icons.Plus className="h-3.5 w-3.5" /> Nouveau site
                </button>
              )}
            </div>
            {!addingSite ? (
              <>
                <Select value={siteId} onValueChange={setSiteId} disabled={!clientId}>
                  <SelectTrigger data-testid="cv-site"><SelectValue placeholder={clientId ? "Choisir un site" : "Sélectionnez d'abord un client"} /></SelectTrigger>
                  <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
                {clientId && sites.length === 0 && <p className="text-xs text-amber-600">Ce client n'a pas encore de site. Créez-en un avec « Nouveau site ».</p>}
              </>
            ) : (
              <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="cv-site-form">
                <div className="space-y-1"><Label className="text-xs">Nom du site *</Label>
                  <Input value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} placeholder="Ex. Villa VAYABOURY" data-testid="cv-site-name" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Adresse</Label>
                    <Input value={siteForm.address} onChange={(e) => setSiteForm({ ...siteForm, address: e.target.value })} data-testid="cv-site-address" /></div>
                  <div className="space-y-1"><Label className="text-xs">Commune</Label>
                    <Input value={siteForm.commune} onChange={(e) => setSiteForm({ ...siteForm, commune: e.target.value })} data-testid="cv-site-commune" /></div>
                </div>
                <div className="space-y-1"><Label className="text-xs">Contact sur place</Label>
                  <Input value={siteForm.on_site_contact} onChange={(e) => setSiteForm({ ...siteForm, on_site_contact: e.target.value })} data-testid="cv-site-onsite" /></div>
                <div className="space-y-1"><Label className="text-xs">Instructions d'accès</Label>
                  <Textarea rows={2} value={siteForm.access_instructions} onChange={(e) => setSiteForm({ ...siteForm, access_instructions: e.target.value })} data-testid="cv-site-access" /></div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setAddingSite(false); setSiteForm(EMPTY_SITE); }} data-testid="cv-site-cancel">Annuler</Button>
                  <Button size="sm" onClick={createSite} disabled={!siteForm.name.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cv-site-save">Ajouter le site</Button>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Date de visite</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cv-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Heure (optionnel)</Label>
              <Input type="time" value={heure} onChange={(e) => setHeure(e.target.value)} data-testid="cv-time" />
            </div>
          </div>
          <p className="-mt-2 text-xs text-slate-400">Choisissez une date à venir pour programmer la visite ; elle apparaîtra dans « Prochaines visites » du tableau de bord.</p>
          {canManage && (
            <div className="space-y-1.5">
              <Label>Affecter à</Label>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger data-testid="cv-assign"><SelectValue placeholder="Choisir la personne en charge" /></SelectTrigger>
                <SelectContent>
                  {user && <SelectItem value={user.id}>Moi — {user.name}</SelectItem>}
                  {users.filter((u) => u.id !== user?.id).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.role === "admin" ? "Admin" : "Commercial"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">Le commercial affecté verra la visite et pourra ouvrir sa fiche, même avant le jour J.</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Prestations concernées</Label>
            <div className="grid grid-cols-1 gap-2">
              {PRESTATION_LIST.map((p) => {
                const active = prestations.includes(p.id);
                const I = Icons[p.icon] || Icons.Square;
                return (
                  <button key={p.id} type="button" onClick={() => toggle(p.id)} data-testid={`cv-prest-${p.id}`}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${active ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600"}`}>
                    <I className="h-4 w-4" /> {p.name}
                    {active && <Icons.Check className="ml-auto h-4 w-4" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={!clientId || !siteId || saving} data-testid="cv-create"
            className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : (date && date !== todayIso() ? "Créer et planifier" : "Créer et remplir")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function VisitList() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canCreate = hasPerm("visits.create");
  const canManageVisits = hasPerm("visits.manage");
  const [params, setParams] = useSearchParams();
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ status: params.get("status") || "", client_id: "", prestation: params.get("prestation") || "", date_from: "", search: "" });
  const [openCreate, setOpenCreate] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [view, setView] = useState("list");
  const preset = { client: params.get("client"), site: params.get("site") };

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);
  useEffect(() => { if (params.get("new")) setOpenCreate(true); }, [params]);

  const load = useCallback(async () => {
    const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== "all"));
    const { data } = await api.get("/visits", { params: clean });
    setVisits(data);
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v === "all" ? "" : v }));

  return (
    <div className="space-y-5" data-testid="visit-list-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 lg:text-3xl">SIGNA CHECK</h1>
          <p className="text-sm text-slate-500">Saisie et suivi des visites avant devis.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            <button onClick={() => setView("list")} data-testid="view-toggle-list"
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${view === "list" ? "bg-emerald-600 text-white" : "text-slate-600"}`}>
              <Icons.List className="h-4 w-4" /> Liste
            </button>
            <button onClick={() => setView("calendar")} data-testid="view-toggle-calendar"
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${view === "calendar" ? "bg-emerald-600 text-white" : "text-slate-600"}`}>
              <Icons.CalendarDays className="h-4 w-4" /> Calendrier
            </button>
          </div>
          {canManageVisits && <Button variant="outline" onClick={() => setOpenImport(true)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="import-report-btn">
            <Icons.FileUp className="mr-1.5 h-4 w-4" /> Importer un rapport PDF
          </Button>}
          {canCreate && <Button onClick={() => setOpenCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-visit-btn">
            <Icons.Plus className="mr-1.5 h-4 w-4" /> Nouvelle visite
          </Button>}
        </div>
      </div>

      <Card className="p-3">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
          <div className="relative col-span-2 lg:col-span-1">
            <Icons.Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={filters.search} onChange={(e) => setF("search", e.target.value)} placeholder="Référence…" className="pl-8" data-testid="filter-search" />
          </div>
          <Select value={filters.status || "all"} onValueChange={(v) => setF("status", v)}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.client_id || "all"} onValueChange={(v) => setF("client_id", v)}>
            <SelectTrigger data-testid="filter-client"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.prestation || "all"} onValueChange={(v) => setF("prestation", v)}>
            <SelectTrigger data-testid="filter-prestation"><SelectValue placeholder="Prestation" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes prestations</SelectItem>
              {PRESTATION_LIST.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <DatePicker value={filters.date_from} onChange={(v) => setF("date_from", v)} testId="filter-date" placeholder="Filtrer par date" />
        </div>
      </Card>

      {view === "calendar" ? (
        <VisitCalendar visits={visits} onOpen={(id) => navigate(`/check/${id}`)} />
      ) : visits.length === 0 ? (
        <EmptyState icon="ClipboardCheck" title="Aucune visite" testId="visits-empty">
          Aucune visite ne correspond. Créez une nouvelle visite pour démarrer un relevé.
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {visits.map((v) => (
            <Card key={v.id} className="cursor-pointer p-4 transition-shadow hover:shadow-md" onClick={() => navigate(`/check/${v.id}`)} data-testid={`visit-row-${v.reference}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700"><Icons.MapPin className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-slate-800"><span className="font-mono-data text-xs text-slate-400">{v.reference}</span> · {v.client_name}</p>
                  <p className="truncate text-sm text-slate-500">{v.site_name} · {fmtDate(v.date)} · {v.author_name}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {v.prestations?.map((p) => <button key={p} onClick={(e) => { e.stopPropagation(); setF("prestation", p); }} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500 transition-colors hover:bg-emerald-100 hover:text-emerald-700" data-testid={`chip-prest-${p}`}>{PRESTATIONS[p]?.name || p}</button>)}
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setF("status", v.status); }} data-testid={`chip-status-${v.status}`} title="Filtrer par ce statut"><StatusBadge status={v.status} /></button>
                <Icons.ChevronRight className="hidden h-4 w-4 text-slate-300 sm:block" />
              </div>
            </Card>
          ))}
        </div>
      )}

      <CreateVisitDialog open={openCreate} onOpenChange={(o) => { setOpenCreate(o); if (!o) setParams({}); }} preset={preset} />
      <ReportImportDialog open={openImport} onOpenChange={setOpenImport} onDone={(r) => r.visit_id && navigate(`/check/${r.visit_id}`)} />
    </div>
  );
}
